/**
 * JoinVideoSessionButton — shared CTA for joining a telehealth video session.
 *
 * Links to the Unify portal join route (`/client-portal/session/{appointmentId}/join`).
 *
 * Design principles:
 *  - No auto-open on mount; only navigates on an explicit user tap/click.
 *  - Accessible: descriptive aria-label, keyboard-navigable.
 *  - Matches existing Mantine button/link conventions in medplum-ui.
 */

import { Button, Tooltip } from '@mantine/core';
import { IconVideo } from '@tabler/icons-react';
import type { ButtonProps } from '@mantine/core';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface JoinVideoSessionButtonProps {
  /** The appointment to join; without one the button is disabled. */
  appointmentId?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  /** When true, the join action is blocked (e.g. outside the client join window). */
  disabled?: boolean;
  /** When set, overrides the disabled tooltip message. */
  disabledReason?: string;
}

/** The portal join route for an appointment. */
export function portalJoinHref(appointmentId: string | undefined): string | undefined {
  return appointmentId ? `/client-portal/session/${appointmentId}/join` : undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Primary CTA for joining a telehealth video session.
 *
 * - Appointment and join open → renders an `<a>` to the portal join route.
 * - Otherwise → disabled button with tooltip.
 */
export function JoinVideoSessionButton({
  appointmentId,
  variant,
  size = 'sm',
  disabled = false,
  disabledReason,
}: JoinVideoSessionButtonProps): JSX.Element {
  const label = 'Join video session';
  if (!appointmentId || disabled) {
    const unavailableMessage =
      disabledReason ?? (!appointmentId ? 'Video session unavailable' : 'Join is not available yet');
    return (
      <Tooltip label={unavailableMessage} withArrow>
        {/* span wrapper required by Tooltip when child is disabled */}
        <span>
          <Button
            size={size}
            variant={variant ?? 'filled'}
            leftSection={<IconVideo size={14} />}
            disabled
            data-testid="join-video-session-btn"
            aria-label={`${label} — ${unavailableMessage}`}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Button
      component="a"
      href={portalJoinHref(appointmentId)}
      size={size}
      variant={variant ?? 'filled'}
      leftSection={<IconVideo size={14} />}
      data-testid="join-video-session-btn"
      aria-label={label}
    >
      {label}
    </Button>
  );
}
